import { ExperienceType } from '../../enums/experience';
import { TagsByStep } from './types';

const PROPERTY_TAGS: TagsByStep = {
    overall: {
        positive: [
            'great location',
            'cleanliness',
            'comfort',
            'amenities',
            'easy check-in',
            'quiet',
            'host helpful',
            'value for money',
            'stylish',
            'family-friendly',
            'workspace',
            'parking',
            'view',
            'safety',
        ],
        negative: [
            'cleanliness',
            'noise',
            'poor communication',
            'maintenance issues',
            'safety concerns',
            'location issues',
            'amenities missing',
            'inaccurate listing',
            'value',
            'heating/cooling',
            'Wi-Fi',
            'pests',
            'accessibility',
        ],
    },
    cleanliness: {
        positive: [
            'sparkling clean',
            'fresh linens',
            'bathroom spotless',
            'kitchen tidy',
            'no odors',
            'trash handled',
            'laundry fresh',
            'well-stocked cleaning supplies',
        ],
        negative: [
            'dirty surfaces',
            'stains',
            'dust',
            'bad odor',
            'mold/mildew',
            'trash left',
            'hair/pet hair',
            'bugs/pests',
        ],
    },
    accuracy: {
        positive: [
            'matches photos',
            'amenities as listed',
            'size as described',
            'view as advertised',
            'neighborhood as described',
            'beds as described',
        ],
        negative: [
            'misleading photos',
            'missing amenities',
            'smaller than expected',
            'layout different',
            'construction not disclosed',
            'incorrect location pin',
            'house rules unclear',
        ],
    },
    communication: {
        positive: [
            'responsive host',
            'clear instructions',
            'friendly',
            'helpful tips',
            'proactive updates',
            'flexible',
            'fast resolution',
        ],
        negative: [
            'slow response',
            'unclear instructions',
            'unavailable',
            'rude',
            'incorrect info',
            'late replies',
            'language barrier',
        ],
    },
    access: {
        positive: [
            'easy check-in',
            'clear directions',
            'parking available',
            'smooth keypad/lockbox',
            'accessible entry',
            'luggage-friendly',
            'safe area',
        ],
        negative: [
            'hard check-in',
            'unclear directions',
            'parking issues',
            'difficult key/lock',
            'stairs/no elevator',
            'noisy area',
            'safety concerns',
        ],
    },
    value: {
        positive: [
            'value for money',
            'quality amenities',
            'comfortable bed',
            'well-equipped kitchen',
            'fast Wi-Fi',
            'good heating/cooling',
            'workspace',
        ],
        negative: [
            'poor value',
            'amenities missing/low quality',
            'uncomfortable bed',
            'weak Wi-Fi',
            'heating/cooling issues',
            'extra fees',
            'maintenance issues',
        ],
    },
};

const BOAT_TAGS: TagsByStep = {
    overall: {
        positive: [
            'scenic route',
            'captain helpful',
            'comfortable seating',
            'safe trip',
            'wildlife sightings',
            'calm waters',
        ],
        negative: [
            'seasick ride',
            'engine noise',
            'crowded',
            'late departure',
            'safety concerns',
            'rough waters',
        ],
    },
    cleanliness: {
        positive: [
            'deck clean',
            'cabin fresh',
            'galley tidy',
            'heads spotless',
            'no bilge odor',
        ],
        negative: [
            'slippery deck',
            'fishy odor',
            'dirty cabin',
            'mold/mildew',
            'trash left',
        ],
    },
    accuracy: {
        positive: [
            'matches photos',
            'capacity as listed',
            'safety gear as listed',
            'equipment as described',
        ],
        negative: [
            'engine type different',
            'equipment missing',
            'smaller than expected',
            'layout different',
        ],
    },
    communication: {
        positive: [
            'clear briefing',
            'responsive crew',
            'helpful tips',
            'timely updates',
        ],
        negative: [
            'no safety briefing',
            'slow replies',
            'unclear instructions',
            'late updates',
        ],
    },
    access: {
        positive: [
            'easy boarding',
            'clear slip number',
            'marina parking',
            'ramp access',
        ],
        negative: [
            'hard to find marina',
            'no parking',
            'boarding difficulties',
            'unclear meeting point',
        ],
    },
    value: {
        positive: [
            'fuel included',
            'gear included',
            'good sound system',
            'shade provided',
        ],
        negative: [
            'fuel surcharge',
            'old equipment',
            'engine issues',
            'overpriced',
        ],
    },
};

const COMMERCIAL_TAGS: TagsByStep = {
    overall: {
        positive: [
            'quiet',
            'great location',
            'fast Wi-Fi',
            'comfortable chairs',
            'friendly host',
            'value for money',
        ],
        negative: [
            'noise',
            'slow Wi-Fi',
            'poor communication',
            'AC issues',
            'overcrowded',
        ],
    },
    cleanliness: {
        positive: [
            'desks clean',
            'restrooms clean',
            'kitchen tidy',
            'bins emptied',
            'no odors',
        ],
        negative: [
            'dirty desks',
            'restroom issues',
            'kitchen mess',
            'bad odor',
            'trash left',
        ],
    },
    accuracy: {
        positive: [
            'matches photos',
            'seating capacity accurate',
            'amenities as listed',
            'meeting rooms as described',
        ],
        negative: [
            'smaller than expected',
            'missing monitors',
            'no whiteboards',
            'layout different',
        ],
    },
    communication: {
        positive: [
            'clear access instructions',
            'responsive host',
            'helpful staff',
        ],
        negative: [
            'badge pickup unclear',
            'slow replies',
            'confusing instructions',
        ],
    },
    access: {
        positive: [
            'easy building entry',
            'access cards ready',
            'elevators available',
            'parking available',
        ],
        negative: [
            'security delays',
            'badge issues',
            'no parking',
            'stairs only',
        ],
    },
    value: {
        positive: [
            'fast Wi-Fi',
            'good AC',
            'meeting rooms',
            'printing included',
            'coffee/tea',
        ],
        negative: [
            'slow Wi-Fi',
            'AC problems',
            'extra fees',
            'no meeting rooms',
        ],
    },
};

const VEHICLE_TAGS: TagsByStep = {
    overall: {
        positive: [
            'smooth ride',
            'fuel efficient',
            'good value',
            'comfortable seats',
            'host helpful',
        ],
        negative: ['maintenance issues', 'noisy', 'poor value', 'odor'],
    },
    cleanliness: {
        positive: [
            'interior clean',
            'exterior clean',
            'no smoke odor',
            'fresh mats',
        ],
        negative: ['dirty interior', 'stains', 'trash left', 'bad odor'],
    },
    accuracy: {
        positive: [
            'model as listed',
            'features as listed',
            'mileage policy clear',
        ],
        negative: [
            'different model',
            'missing features',
            'fuel policy unclear',
        ],
    },
    communication: {
        positive: [
            'quick pickup coordination',
            'responsive host',
            'clear instructions',
        ],
        negative: ['slow replies', 'unclear pickup', 'late responses'],
    },
    access: {
        positive: [
            'easy pickup',
            'contactless check-in',
            'clear drop-off',
            'nearby parking',
        ],
        negative: ['long wait', 'hard to find location', 'key issues'],
    },
    value: {
        positive: [
            'child seat included',
            'good mileage allowance',
            'USB/CarPlay',
            'spare included',
        ],
        negative: ['extra fees', 'low fuel', 'high mileage charges'],
    },
};

const ACTIVITY_TAGS: TagsByStep = {
    overall: {
        positive: [
            'great guide',
            'memorable',
            'fun',
            'well organized',
            'safe',
            'value for money',
        ],
        negative: [
            'overpriced',
            'disorganized',
            'boring',
            'safety concerns',
            'overcrowded',
        ],
    },
    cleanliness: {
        positive: ['equipment clean', 'facility tidy', 'no odors'],
        negative: ['dirty equipment', 'messy facility', 'bad odor'],
    },
    accuracy: {
        positive: [
            'matches description',
            'duration as listed',
            'difficulty accurate',
        ],
        negative: [
            'shorter than listed',
            'harder than described',
            'misleading photos',
        ],
    },
    communication: {
        positive: [
            'clear meeting point',
            'on-time start',
            'friendly staff',
            'good briefing',
        ],
        negative: ['unclear instructions', 'late start', 'poor briefing'],
    },
    access: {
        positive: ['easy check-in', 'clear directions', 'near transit'],
        negative: ['hard to find', 'long wait', 'parking issues'],
    },
    value: {
        positive: ['quality gear', 'small group', 'extras included'],
        negative: ['gear missing', 'overcrowded', 'extra charges'],
    },
};

export const TAGS_BY_TYPE: Record<ExperienceType, TagsByStep> = {
    [ExperienceType.Property]: PROPERTY_TAGS,
    [ExperienceType.Boat]: BOAT_TAGS,
    [ExperienceType.Commercial]: COMMERCIAL_TAGS,
    [ExperienceType.Vehicle]: VEHICLE_TAGS,
    [ExperienceType.Activity]: ACTIVITY_TAGS,
};
